import { IConversionApiField, IConversionApiNormalizerOptions } from './form-conversion.model';
import { CountryCode, parsePhoneNumberFromString as parsePhone } from 'libphonenumber-js';
import { UsStates } from '../../_shared/data/us-states.data';
import { Iso31661 } from '../../_shared/data/iso-3166-1.data';
import { UtilityService } from '../../_shared/services/utility.service';

export class ConversionApiNormalizerService {
    private _countryCode: string | undefined = undefined;
    private _fields: IConversionApiField[];
    private _utilService: UtilityService = new UtilityService();

    constructor(fields: IConversionApiField[], countryField?: string) {
        this._fields = fields;

        if (countryField) {
            const country = fields.find((field) => field.name === countryField)?.value;
            this._countryCode = country ? Iso31661.getAlpha2(country) : undefined;
        }
    }

    async getNormalizedFields() {
        const normalized: { [key: string]: string | undefined } = Object.values(
            this._fields
        ).reduce((fields, field) => {
            if (!field.value) return { ...fields, [field.name]: undefined };

            const normalizer = field.options.normalizer || 'default';
            const value = this[normalizer](field.value);

            return { ...fields, [field.name]: value || undefined };
        }, {});

        const hashed = Object.fromEntries(
            await Promise.all(
                Object.entries(normalized)
                    .filter(([key, value]) => value && key.startsWith('sha256_'))
                    .map(async ([key, value]) => {
                        value = value ? await this._utilService.digestMessage(value) : value;
                        return [key, value];
                    })
            )
        );

        return { ...normalized, ...hashed };
    }

    default(value: string): string {
        return this.normalize(value);
    }

    state(value: string): string {
        if (this._countryCode === 'us') return this.normalize(UsStates.getStateAbbreviation(value));

        return this.normalize(value, { removePunctuation: true, trim: 'all' });
    }

    country(value: string): string {
        const countryCode = Iso31661.getAlpha2(value);

        return this.normalize(countryCode);
    }

    email(value: string): string {
        const email = this.normalizeGmail(value);

        return this.normalize(email, { lowerCase: true, trim: 'all' });
    }

    phone(value: string): string {
        const phone = this.phone_e164(value);
        return phone.replace(/[+]/g, '');
    }

    phone_e164(value: string): string {
        return this.normalize(this.e164(value), { trim: 'all' });
    }

    zip(value: string): string {
        const zip = this.normalize(value, { removePunctuation: true, trim: 'all' });
        if (this._countryCode !== 'us') return zip;

        return zip.substring(0, 5);
    }

    normalize(value: string, options: Partial<IConversionApiNormalizerOptions> = {}): string {
        const { lowerCase = true, trim = 'default', removePunctuation = false } = options;

        if (lowerCase) value = value.toLowerCase();
        if (trim) value = trim === 'default' ? value.trim() : this.intermediateTrim(value);
        if (removePunctuation) value = this.removePunctuation(value);

        return value;
    }

    e164(value: string): string {
        const _value = value.replace(/^0+/, '');
        const phone = this.normalize(_value, { trim: 'all' });
        const e164 = parsePhone(phone, this._countryCode as CountryCode)?.formatInternational();

        if (e164) return e164;

        const fakePhone = this.removePunctuationWithoutPlus(phone);
        return `+${parseInt(fakePhone)}`;
    }

    intermediateTrim(value: string): string {
        return value.replace(/\s+/g, '');
    }

    removePunctuationWithoutPlus(value: string): string {
        return value.replace(/[.,?!()'"-]/g, '');
    }

    removePunctuation(value: string): string {
        const _value = this.removePunctuationWithoutPlus(value);
        return _value.replace(/[+]/g, '');
    }

    normalizeGmail(email: string): string {
        const emailParts = email.split('@');

        if (emailParts.length > 1 && /^(gmail|googlemail)\.com\s*/.test(emailParts[1])) {
            emailParts[0] = emailParts[0].replace(/\./g, '');
            email = `${emailParts[0]}@${emailParts[1]}`;
        }

        return email;
    }
}
